// PeptideVault v6.5 — Protocol Planner + Scenario Planning
import { isOngoingProtocol } from './protocol-views.js'

// ══════════════════════════════════════════════════════
// PROTOCOL PLANNER
// ══════════════════════════════════════════════════════

function protocolFreqLabel(f, p) {
  if (p && p.scheduleMode === 'explicit' && (p.explicitEntries || []).length) return 'Imported Exact Schedule'
  return { daily:'Daily', eod:'Every Other Day', '3x-week':'3× / Week', weekly:'Weekly', biweekly:'Biweekly', custom:'Custom' }[f] || f
}

function protocolStatusBadge(status) {
  const colors = { active:'green', upcoming:'yellow', completed:'blue', paused:'amber' }
  return '<span class="badge badge-' + (colors[status]||'gray') + '">' + (status||'upcoming') + '</span>'
}

function protocolLifecycleDates(p) {
  var dates = []
  ;(p.explicitEntries || []).forEach(function(e) { if (e && e.date) dates.push(e.date) })
  ;(p.sourceExactSchedule || []).forEach(function(d) { if (d) dates.push(d) })
  ;(p.components || []).forEach(function(c) { (c.explicitEntries || []).forEach(function(e) { if (e && e.date) dates.push(e.date) }) })
  if (!dates.length) { if (p.startDate) dates.push(p.startDate); if (p.endDate) dates.push(p.endDate) }
  dates.sort()
  return { first:dates[0] || p.startDate || '', last:dates[dates.length - 1] || p.endDate || '' }
}

function automaticProtocolStatus(p, today) {
  today = today || localDateKey()
  if (p.isSupplementSchedule) return 'supplement'
  if (p.status === 'paused') return 'paused'
  if (isOngoingProtocol(p)) return 'active'
  var dates = protocolLifecycleDates(p)
  if (dates.last && dates.last < today) return 'completed'
  if (dates.first && dates.first > today) return 'upcoming'
  return 'active'
}

function reconcileProtocolLifecycle(today) {
  today = today || localDateKey()
  var changed = []
  ;(S.protocols || []).forEach(function(p) {
    if (p.archived || p.supersededByImport) return
    var previous = p.status || 'upcoming'
    var next = automaticProtocolStatus(p, today)
    if (previous !== next) {
      p.status = next
      // Lifecycle status is derived from the schedule. Keep its timestamp
      // separate so opening a device later cannot look like a newer user edit.
      p.lifecycleStatusAt = now()
      p.lifecycleStatusSource = 'automatic'
      if (next === 'completed') {
      p.completedAt = p.completedAt || now()
      p.autoArchivedAt = now()
      p.reservationReviewRequired = (S.inventoryReservations || []).some(function(r) { return r.protocolId === p.id && r.workflowStatus !== 'completed' && Number(r.qty != null ? r.qty : r.quantity) > 0 })
      if (typeof logAuditEvent === 'function') logAuditEvent('protocol', p.id, 'auto-complete', 'Automatically completed and archived ' + (p.name || p.peptideName || 'protocol') + ' after end date ' + p.endDate)
      } else {
        p.completedAt = ''
        p.autoArchivedAt = ''
        if (typeof logAuditEvent === 'function') logAuditEvent('protocol', p.id, 'auto-status', 'Automatically changed ' + (p.name || p.peptideName || 'protocol') + ' from ' + previous + ' to ' + next)
      }
      changed.push(p)
    }
  })
  return changed
}

function toggleProtocolHistory() { S._showProtocolHistory = !S._showProtocolHistory; render() }

function protocolDaysRemaining(p) {
  if (!p.endDate) return null
  const today = localDateKey()
  if (p.endDate < today) return 0
  const d1 = new Date(today), d2 = new Date(p.endDate)
  return Math.ceil((d2 - d1) / 86400000)
}

function parseLocalDate(dateStr) {
  // Parse YYYY-MM-DD as LOCAL date, not UTC
  var parts = dateStr.split('-')
  return new Date(+parts[0], +parts[1] - 1, +parts[2])
}

function formatLocalDate(d) {
  var y = d.getFullYear()
  var m = String(d.getMonth() + 1).padStart(2, '0')
  var day = String(d.getDate()).padStart(2, '0')
  return y + '-' + m + '-' + day
}

function protocolScheduleDates(p) {
  if (p && p.scheduleMode === 'explicit' && (p.explicitEntries || []).length) {
    return p.explicitEntries.filter(function(e) { return e && e.date }).map(function(e) { return e.date }).sort()
  }
  if (!p.startDate) return []
  var start = parseLocalDate(p.startDate)
  var end = p.endDate ? parseLocalDate(p.endDate) : new Date(start.getFullYear(), start.getMonth(), start.getDate() + 90)
  var dates = []
  var freq = p.frequency || 'daily'
  var weekdays = p.weekdays || []
  var breaks = p.breaks || []
  var startDow = start.getDay()
  var dayCount = 0

  for (var d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    var key = formatLocalDate(d)
    // Check breaks
    if (breaks.some(function(b) { return key >= b.startDate && key <= b.endDate })) { dayCount++; continue }
    var dow = d.getDay()
    if (freq === 'daily') dates.push(key)
    else if (freq === 'eod' && dayCount % 2 === 0) dates.push(key)
    else if (freq === '3x-week' && [1,3,5].indexOf(dow) >= 0) dates.push(key)
    else if (freq === 'weekly' && dow === startDow) dates.push(key)
    else if (freq === 'biweekly' && dow === startDow && dayCount % 14 < 7) dates.push(key)
    else if (freq === 'custom' && weekdays.indexOf(dow) >= 0) dates.push(key)
    dayCount++
  }
  return dates
}

function protocolTotalDoses(p) { return protocolScheduleDates(p).length }

function protocolDoseInMg(doseAmount, doseUnit) {
  var amt = parseFloat(doseAmount || 0)
  if (!amt) return 0
  var unit = doseUnit || 'mcg'
  if (unit === 'mg') return amt
  if (unit === 'mcg') return amt / 1000
  return null  // IU/units cannot be converted without concentration
}

function protocolPlanningNeed(p, horizon) {
  var events = resolveProtocolSchedule(p)
  var total = 0
  var unit = ''
  var incompatible = false
  events.forEach(function(event) {
    if (horizon && (event.date < horizon.start || event.date > horizon.end)) return
    var dose = event.planningDose || planningDose(event.doseAmount, event.doseUnit || p.doseUnit)
    if (!dose) { incompatible = true; return }
    if (!unit) unit = dose.unit
    if (unit !== dose.unit) { incompatible = true; return }
    total += dose.amount
  })
  if (incompatible || !unit || total <= 0) return null
  return { amount:+total.toFixed(unit === 'IU' ? 2 : 4), unit:unit, doses:events.filter(function(event) { return !horizon || (event.date >= horizon.start && event.date <= horizon.end) }).length }
}

function protocolNeedLabel(need) {
  return need ? need.amount + ' ' + need.unit : 'Unavailable'
}

function protocolMgNeeded(p) {
  var need = protocolPlanningNeed(p)
  if (need && need.unit === 'mg') return +need.amount.toFixed(2)
  // Imported exact schedules carry authoritative per-date doses.
  if (p && p.scheduleMode === 'explicit' && (p.explicitEntries || []).length) {
    var explicitTotal = 0
    ;(p.explicitEntries || []).forEach(function(e) {
      var mg = protocolDoseInMg(e.doseAmount, e.doseUnit || p.doseUnit)
      if (mg !== null) explicitTotal += mg
    })
    return +explicitTotal.toFixed(2)
  }
  // Phase-aware: if phases exist, sum per-phase doses
  var phases = p.phases || []
  if (phases.length > 0) {
    var total = 0
    var scheduleDates = protocolScheduleDates(p)
    for (var i = 0; i < scheduleDates.length; i++) {
      var dateStr = scheduleDates[i]
      var start = parseLocalDate(p.startDate)
      var current = parseLocalDate(dateStr)
      var dayNum = Math.round((current - start) / 86400000)
      // Find the active phase for this day
      var activePhase = null
      for (var j = 0; j < phases.length; j++) {
        if (dayNum >= (phases[j].startDay||0) && dayNum <= (phases[j].endDay||9999)) {
          activePhase = phases[j]; break
        }
      }
      var dose = activePhase ? protocolDoseInMg(activePhase.doseAmount, activePhase.doseUnit || p.doseUnit) : protocolDoseInMg(p.doseAmount, p.doseUnit)
      if (dose !== null) total += dose
    }
    return +total.toFixed(2)
  }
  // No phases — use top-level dose
  var doses = protocolTotalDoses(p)
  var mgPerDose = protocolDoseInMg(p.doseAmount, p.doseUnit)
  if (mgPerDose === null) return 0  // IU/units — can't calculate mg
  return +(mgPerDose * doses).toFixed(2)
}

function protocolPlanningHorizon(date) {
  var start = date ? new Date(date) : new Date()
  start = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  var end = new Date(start.getFullYear(), start.getMonth() + 3, 0)
  return { start:formatLocalDate(start), end:formatLocalDate(end), days:Math.max(1,Math.round((end-start)/86400000)+1) }
}

function protocolMgNeededInHorizon(p, horizon) {
  var need = protocolPlanningNeed(p, horizon || protocolPlanningHorizon())
  return need && need.unit === 'mg' ? +need.amount.toFixed(2) : 0
}

function protocolVialsNeeded(p, skuOrStrength, horizon) {
  var need = protocolPlanningNeed(p, horizon)
  if (!need) return 0
  var strength = typeof skuOrStrength === 'number' ? (need.unit === 'mg' ? skuOrStrength : null) : skuStrengthForUnit(skuOrStrength, need.unit)
  if (!strength || strength <= 0) return 0
  return Math.ceil(need.amount / strength)
}

function protocolCanCalculateMg(p) {
  var need = protocolPlanningNeed(p)
  return !!need && need.unit === 'mg'
}

// ── Gantt-style timeline SVG ──
function renderProtocolGantt(protocols) {
  if (!protocols.length) return ''
  const today = localDateKey()
  const allDates = protocols.flatMap(p => [p.startDate, p.endDate].filter(Boolean))
  if (allDates.length === 0) return ''

  const minDate = allDates.reduce((a,b) => a < b ? a : b)
  const maxDate = allDates.reduce((a,b) => a > b ? a : b)
  const d1 = parseLocalDate(minDate), d2 = parseLocalDate(maxDate)
  const totalDays = Math.max(1, Math.ceil((d2 - d1) / 86400000))

  const w = 700, rowH = 36, pad = 140, h = protocols.length * rowH + 40
  const barW = w - pad - 20

  function dayX(dateStr) {
    const diff = Math.ceil((parseLocalDate(dateStr) - d1) / 86400000)
    return pad + (diff / totalDays) * barW
  }

  let svg = '<svg viewBox="0 0 ' + w + ' ' + h + '" style="width:100%;max-height:' + Math.max(200, h) + 'px">'

  // Today line
  if (today >= minDate && today <= maxDate) {
    const tx = dayX(today)
    svg += '<line x1="' + tx + '" y1="0" x2="' + tx + '" y2="' + h + '" stroke="var(--red)" stroke-width="1" stroke-dasharray="4,3"/>'
    svg += '<text x="' + tx + '" y="12" text-anchor="middle" font-size="9" fill="var(--red)">Today</text>'
  }

  const colors = ['#4F46E5','#059669','#D97706','#DC2626','#7C3AED','#0891B2','#CA8A04','#BE185D']

  protocols.forEach(function(p, i) {
    const y = 20 + i * rowH
    const color = colors[i % colors.length]

    // Label
    svg += '<text x="4" y="' + (y + 18) + '" font-size="11" font-weight="600" fill="var(--text2)">' + esc(p.name || p.peptideName || 'Protocol').slice(0,18) + '</text>'
    svg += '<text x="4" y="' + (y + 30) + '" font-size="9" fill="var(--text4)">' + protocolFreqLabel(p.frequency, p) + '</text>'

    // Bar
    if (p.startDate && p.endDate) {
      const x1 = dayX(p.startDate), x2 = dayX(p.endDate)
      svg += '<rect x="' + x1 + '" y="' + (y + 4) + '" width="' + Math.max(4, x2-x1) + '" height="22" rx="4" fill="' + color + '" opacity="0.8"/>'

      // Phase markers
      ;(p.phases||[]).forEach(function(ph) {
        if (ph.startDay != null && ph.endDay != null) {
          const ps = new Date(parseLocalDate(p.startDate).getTime() + ph.startDay * 86400000).toISOString().slice(0,10)
          const pe = new Date(parseLocalDate(p.startDate).getTime() + ph.endDay * 86400000).toISOString().slice(0,10)
          const px1 = dayX(ps), px2 = dayX(pe)
          svg += '<rect x="' + px1 + '" y="' + (y + 4) + '" width="' + Math.max(2, px2-px1) + '" height="22" rx="2" fill="' + color + '" opacity="0.4" stroke="' + color + '" stroke-width="1"/>'
          svg += '<text x="' + ((px1+px2)/2) + '" y="' + (y + 19) + '" text-anchor="middle" font-size="8" fill="white">' + esc(ph.name||'').slice(0,10) + '</text>'
        }
      })

      // Break markers
      ;(p.breaks||[]).forEach(function(br) {
        if (br.startDate && br.endDate) {
          const bx1 = dayX(br.startDate), bx2 = dayX(br.endDate)
          svg += '<rect x="' + bx1 + '" y="' + (y + 4) + '" width="' + Math.max(2, bx2-bx1) + '" height="22" fill="var(--bg)" opacity="0.7"/>'
          svg += '<line x1="' + bx1 + '" y1="' + (y+4) + '" x2="' + bx2 + '" y2="' + (y+26) + '" stroke="var(--text4)" stroke-width="0.5" stroke-dasharray="2,2"/>'
        }
      })
    }

    svg += '<text x="' + (w-18) + '" y="' + (y + 18) + '" text-anchor="end" font-size="9" fill="var(--text4)">' + esc(p.doseAmount || '') + ' ' + esc(p.doseUnit || '') + '</text>'
  })

  svg += '</svg>'
  return svg
}

function renderProtocolPlannerTab() {
  const protocols = (S.protocols || []).filter(function(p) { return !p.isSupplementSchedule }).slice().sort(function(a,b) {
    const order = { active:0, upcoming:1, paused:2, completed:3 }
    return (order[a.status]??9) - (order[b.status]??9) || (a.startDate||'').localeCompare(b.startDate||'')
  })
  const today = localDateKey()
  const horizon = protocolPlanningHorizon()
  const active = protocols.filter(function(p) { return p.status === 'active' && (!p.startDate || p.startDate <= today) && (!p.endDate || p.endDate >= today) })
  const upcoming = protocols.filter(function(p) { return p.status === 'upcoming' && p.startDate && p.startDate > today && p.startDate <= horizon.end })
  const paused = protocols.filter(function(p) { return p.status === 'paused' })
  const history = protocols.filter(function(p) { return p.status === 'completed' })
  const currentProtocols = protocols.filter(function(p) { return p.status === 'active' || p.status === 'paused' || (p.status === 'upcoming' && (!p.startDate || p.startDate <= horizon.end)) })
  const displayedProtocols = S._showProtocolHistory ? currentProtocols.concat(history) : currentProtocols

  function labsForProtocol(p) {
    return (S.labResults||[]).filter(function(l){return p.startDate && p.endDate && l.date >= p.startDate && l.date <= p.endDate}).length
  }
  function plannerStatus(p) {
    var map = { active:'green', upcoming:'yellow', completed:'blue', paused:'amber' }
    return '<span class="badge badge-' + (map[p.status]||'gray') + '"><i></i>' + esc((p.status||'upcoming').replace(/^./,function(c){return c.toUpperCase()})) + '</span>'
  }

  return '<div class="tab-content planner-workspace" style="animation:fadeIn .25s ease">' +
    '<div class="planner-page-header">' +
      '<div><h2>Protocol Planner</h2><p>Plan, review, and manage peptide protocols</p></div>' +
      '<div class="planner-page-actions"><button class="btn btn-secondary btn-sm" onclick="setTab(\'timeline\')">' + ICONS.calendar + ' Timeline</button><button class="btn btn-primary btn-sm" onclick="openModal(\'addProtocol\')">' + ICONS.plus + ' New Protocol</button></div>' +
    '</div>' +

    '<div class="planner-summary-card">' +
      '<div class="planner-summary-title">Protocol Overview</div>' +
      '<div class="planner-summary-metric"><strong>' + active.length + '</strong><span>Active</span></div>' +
      '<div class="planner-summary-metric"><strong>' + upcoming.length + '</strong><span>Upcoming</span></div>' +
      '<div class="planner-summary-metric"><strong>' + paused.length + '</strong><span>Paused</span></div>' +
      '<div class="planner-summary-metric"><strong>' + protocols.length + '</strong><span>Total</span></div>' +
    '</div>' +

    '<div class="card protocol-list-card">' +
      '<div class="protocol-list-grid protocol-list-header">' +
        '<span>Protocol</span><span>Frequency</span><span>Dates</span><span>Doses</span><span>Status</span><span>Labs</span><span class="protocol-actions-header">Actions</span>' +
      '</div>' +
      (displayedProtocols.length === 0 && history.length === 0 ?
        '<div class="empty-state planner-empty"><div class="empty-icon">📋</div><p>No protocols yet</p><p>Create a protocol to plan schedules, calculate inventory needs, and visualize timelines.</p><button class="btn btn-primary btn-sm" onclick="openModal(\'addProtocol\')">' + ICONS.plus + ' New Protocol</button></div>' :
        displayedProtocols.map(function(p) {
          var rem = protocolDaysRemaining(p)
          var doses = protocolTotalDoses(p)
          var planningNeed = protocolPlanningNeed(p)
          var labs = labsForProtocol(p)
          var explicit = p.scheduleMode === 'explicit' && (p.explicitEntries||[]).length
          var dateCount = protocolScheduleDates(p).length
          return '<div class="protocol-list-grid protocol-list-row">' +
            '<div class="protocol-name-cell">' +
              '<div class="protocol-primary">' + esc(p.name || p.peptideName || 'Untitled') + '</div>' +
              '<div class="protocol-secondary">' + esc(p.peptideName || '') + (planningNeed ? ' · ' + planningNeed.amount + ' ' + planningNeed.unit + ' total' : '') + '</div>' +
            '</div>' +
            '<div class="protocol-frequency-cell"><span>' + protocolFreqLabel(p.frequency, p) + '</span>' + (explicit ? '<small><span class="source-dot"></span>Imported exact</small>' : '<small>Planned schedule</small>') + '</div>' +
            '<div class="protocol-date-cell"><span class="mono">' + (p.startDate ? p.startDate.slice(5) : '—') + ' → ' + (p.endDate ? p.endDate.slice(5) : '—') + '</span><small>' + dateCount + ' planned date' + (dateCount===1?'':'s') + '</small></div>' +
            '<div class="protocol-dose-cell"><span class="mono">' + doses + '</span> doses' + (rem != null && rem > 0 ? '<small>' + rem + ' days left</small>' : '<small>&nbsp;</small>') + '</div>' +
            '<div class="protocol-status-cell">' + plannerStatus(p) + '</div>' +
            '<div class="protocol-labs-cell">' + (labs ? '<span class="mono">' + labs + '</span> lab' + (labs>1?'s':'') : '<span class="muted">—</span>') + '</div>' +
            '<div class="protocol-actions">' +
              '<button class="btn btn-ghost btn-xs" onclick="openModal(\'editProtocol\',{protocolId:\'' + p.id + '\'})" title="Edit">' + ICONS.edit + '</button>' +
              '<button class="btn btn-ghost btn-xs" onclick="cloneProtocolAsScenario(\'' + p.id + '\')" title="Clone as Scenario">📋</button>' +
              '<button class="btn btn-ghost btn-xs" onclick="deleteProtocol(\'' + p.id + '\')" title="Delete" style="color:var(--red)">✕</button>' +
            '</div>' +
          '</div>'
        }).join('')
      ) +
      '<div class="planner-table-footer"><span>Showing ' + displayedProtocols.length + ' protocol' + (displayedProtocols.length===1?'':'s') + '</span>' + (history.length?'<button class="btn btn-ghost btn-xs" onclick="toggleProtocolHistory()">' + (S._showProtocolHistory?'Hide':'Show') + ' History (' + history.length + ')</button>':'') + '</div>' +
    '</div>' +

    (active.length > 0 ?
      '<div class="card planner-timeline-card"><div class="planner-section-heading"><div><strong>Active Timeline</strong><span>Current protocol windows at a glance</span></div><button class="btn btn-ghost btn-xs" onclick="setTab(\'timeline\')">Open full timeline →</button></div>' + renderProtocolGantt(active) + '</div>' : '') +
  '</div>'
}

// ══════════════════════════════════════════════════════
// SCENARIO PLANNING
// ══════════════════════════════════════════════════════

function cloneProtocolAsScenario(protocolId) {
  var p = (S.protocols||[]).find(function(x) { return x.id === protocolId })
  if (!p) { showToast('Protocol not found'); return }
  var scenario = {
    id: uid(),
    name: p.name + ' — Scenario',
    baseProtocolId: protocolId,
    status: 'draft',
    peptideId: p.peptideId,
    peptideName: p.peptideName,
    startDate: p.startDate,
    endDate: p.endDate,
    frequency: p.frequency,
    weekdays: (p.weekdays||[]).slice(),
    doseAmount: p.doseAmount,
    doseUnit: p.doseUnit,
    phases: (p.phases||[]).map(function(ph) { return Object.assign({}, ph) }),
    breaks: (p.breaks||[]).map(function(br) { return Object.assign({}, br) }),
    notes: 'Cloned from: ' + (p.name || 'protocol'),
    createdAt: now()
  }
  if (!S.scenarios) S.scenarios = []
  S.scenarios.push(scenario)
  save()
  render()
  showToast('Scenario created from ' + esc(p.name))
}

function deleteScenario(id) {
  S.scenarios = (S.scenarios||[]).filter(function(s) { return s.id !== id })
  save(); render()
  showToast('Scenario deleted')
}

function renderScenarioPlanningTab() {
  var scenarios = (S.scenarios || [])
  var protocols = (S.protocols || [])

  return '<div class="tab-content" style="animation:fadeIn .25s ease">' +
    '<div class="row" style="margin-bottom:16px;flex-wrap:wrap;gap:8px">' +
      '<h2 style="font-size:20px;font-weight:700">Scenario Planning</h2>' +
      '<div class="spacer"></div>' +
      '<button class="btn btn-secondary btn-sm" onclick="openModal(\'addScenario\')">' + ICONS.plus + ' New Scenario</button>' +
    '</div>' +
    '<div style="font-size:13px;color:var(--text3);margin-bottom:16px">' +
      'Clone a protocol and adjust start date, frequency, duration, or dose to compare alternatives without modifying the active plan.' +
    '</div>' +

    (scenarios.length === 0 ?
      '<div class="empty-state"><div class="empty-icon">🔬</div><p>No scenarios</p><p style="font-size:12px;color:var(--text4)">Go to Protocol Planner and clone a protocol to start what-if planning.</p></div>' :

      // Side-by-side comparison
      scenarios.map(function(sc) {
        var base = protocols.find(function(p) { return p.id === sc.baseProtocolId })
        var baseDoses = base ? protocolTotalDoses(base) : 0
        var baseMg = base ? protocolMgNeeded(base) : 0
        var scDoses = protocolTotalDoses(sc)
        var scMg = protocolMgNeeded(sc)
        var doseDiff = scDoses - baseDoses
        var mgDiff = scMg - baseMg

        return '<div class="card" style="margin-bottom:14px;padding:16px">' +
          '<div class="row" style="margin-bottom:12px;align-items:center">' +
            '<div style="font-size:15px;font-weight:700">' + esc(sc.name) + '</div>' +
            '<div class="spacer"></div>' +
            '<button class="btn btn-ghost btn-xs" onclick="openModal(\'editScenario\',{scenarioId:\'' + sc.id + '\'})">' + ICONS.edit + '</button>' +
            '<button class="btn btn-ghost btn-xs" onclick="promoteScenario(\'' + sc.id + '\')" title="Promote to Protocol" style="color:var(--green)">✓</button>' +
            '<button class="btn btn-ghost btn-xs" onclick="deleteScenario(\'' + sc.id + '\')" style="color:var(--red)">✕</button>' +
          '</div>' +

          '<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">' +
            // Base protocol column
            '<div style="background:var(--bg3);border-radius:var(--r2);padding:12px">' +
              '<div style="font-size:11px;font-weight:700;color:var(--text4);text-transform:uppercase;margin-bottom:8px">Base Protocol</div>' +
              (base ?
                '<div style="font-size:13px;font-weight:600">' + esc(base.name) + '</div>' +
                '<div style="font-size:12px;color:var(--text3);margin-top:4px">' +
                  protocolFreqLabel(base.frequency) + ' · ' + esc(base.doseAmount||'') + ' ' + esc(base.doseUnit||'') +
                '</div>' +
                '<div style="font-size:12px;color:var(--text3)">' + (base.startDate||'?') + ' → ' + (base.endDate||'?') + '</div>' +
                '<div style="font-size:12px;margin-top:6px">' + baseDoses + ' doses · ' + baseMg + ' mg</div>'
              : '<div style="color:var(--text4);font-size:12px">Base protocol deleted</div>') +
            '</div>' +

            // Scenario column
            '<div style="background:var(--accentL);border-radius:var(--r2);padding:12px">' +
              '<div style="font-size:11px;font-weight:700;color:var(--accent);text-transform:uppercase;margin-bottom:8px">Scenario</div>' +
              '<div style="font-size:13px;font-weight:600">' + esc(sc.name) + '</div>' +
              '<div style="font-size:12px;color:var(--text3);margin-top:4px">' +
                protocolFreqLabel(sc.frequency) + ' · ' + esc(sc.doseAmount||'') + ' ' + esc(sc.doseUnit||'') +
              '</div>' +
              '<div style="font-size:12px;color:var(--text3)">' + (sc.startDate||'?') + ' → ' + (sc.endDate||'?') + '</div>' +
              '<div style="font-size:12px;margin-top:6px">' + scDoses + ' doses · ' + scMg + ' mg</div>' +
              (doseDiff !== 0 || mgDiff !== 0 ?
                '<div style="font-size:11px;margin-top:4px;color:' + (mgDiff > 0 ? 'var(--amber)' : 'var(--green)') + '">' +
                  (doseDiff > 0 ? '+' : '') + doseDiff + ' doses · ' + (mgDiff > 0 ? '+' : '') + mgDiff.toFixed(1) + ' mg' +
                '</div>' : '') +
            '</div>' +
          '</div>' +

          // Combined Gantt
          (base ? '<div style="margin-top:12px">' + renderProtocolGantt([base, sc]) + '</div>' : '') +
        '</div>'
      }).join('')
    ) +
  '</div>'
}

function promoteScenario(id) {
  var sc = (S.scenarios||[]).find(function(s) { return s.id === id })
  if (!sc) return
  var protocol = Object.assign({}, sc, { id: uid(), status: 'active', notes: 'Promoted from scenario: ' + sc.name, createdAt: now() })
  delete protocol.baseProtocolId
  if (!S.protocols) S.protocols = []
  S.protocols.push(protocol)
  S.scenarios = S.scenarios.filter(function(s) { return s.id !== id })
  save(); render()
  showToast('Scenario promoted to active protocol')
}

function saveProtocol(data) {
  if (!S.protocols) S.protocols = []
  if (data.id) {
    var idx = S.protocols.findIndex(function(p) { return p.id === data.id })
    if (idx >= 0) S.protocols[idx] = Object.assign(S.protocols[idx], data)
    else S.protocols.push(data)
  } else {
    data.id = uid()
    data.createdAt = now()
    S.protocols.push(data)
  }
  save(); closeModal(); render()
  showToast('Protocol saved')
}

function deleteProtocol(id) {
  S.protocols = (S.protocols||[]).filter(function(p) { return p.id !== id })
  // Also clean up any reservations
  S.inventoryReservations = (S.inventoryReservations||[]).filter(function(r) { return r.protocolId !== id })
  S.protocolDependencies = (S.protocolDependencies||[]).filter(function(d) { return d.protocolId !== id && d.relatedProtocolId !== id })
  save(); render()
  showToast('Protocol deleted')
}

function saveScenario(data) {
  if (!S.scenarios) S.scenarios = []
  if (data.id) {
    var idx = S.scenarios.findIndex(function(s) { return s.id === data.id })
    if (idx >= 0) S.scenarios[idx] = Object.assign(S.scenarios[idx], data)
    else S.scenarios.push(data)
  } else {
    data.id = uid()
    data.createdAt = now()
    S.scenarios.push(data)
  }
  save(); closeModal(); render()
  showToast('Scenario saved')
}

export {
  protocolFreqLabel,
  protocolStatusBadge,
  protocolLifecycleDates,
  automaticProtocolStatus,
  reconcileProtocolLifecycle,
  toggleProtocolHistory,
  protocolDaysRemaining,
  parseLocalDate,
  formatLocalDate,
  protocolScheduleDates,
  protocolTotalDoses,
  protocolDoseInMg,
  protocolPlanningNeed,
  protocolNeedLabel,
  protocolMgNeeded,
  protocolPlanningHorizon,
  protocolMgNeededInHorizon,
  protocolVialsNeeded,
  protocolCanCalculateMg,
  renderProtocolGantt,
  renderProtocolPlannerTab,
  cloneProtocolAsScenario,
  deleteScenario,
  renderScenarioPlanningTab,
  promoteScenario,
  saveProtocol,
  deleteProtocol,
  saveScenario
}
